import { last } from 'lodash';
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Define the context type
interface BackButtonContextType {
  isVisible: boolean;
  hasInAppHistory: boolean;
  setIsVisible: (isVisible: boolean) => void;
  goBack: (fallbackPath?: string) => void;
}

// Create context with default values
const BackButtonContext = createContext<BackButtonContextType | undefined>(
  undefined
);

// Create provider component props type
interface BackButtonProviderProps {
  children: ReactNode;
}

// Create provider component
export const BackButtonProvider: React.FC<BackButtonProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [fromExternal, setFromExternal] = useState(false);

  useEffect(() => {
    // Check if this is the first page load (potential external navigation)
    if (history.length === 0) {
      // If there's a referrer from a different origin, mark as external
      const referrer = document.referrer;
      const currentOrigin = window.location.origin;
      const hasExternalReferrer =
        referrer !== '' && !referrer.startsWith(currentOrigin);
      setFromExternal(hasExternalReferrer);
    }

    setHistory((prevHistory) => [...prevHistory, location.pathname]);
  }, [location.pathname]);

  // The previous page (second to last item in history)
  const previousPage = history[history.length - 2];

  useEffect(() => {
    if (previousPage === '/login' || history.length <= 1 || fromExternal) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [history, fromExternal, previousPage]);

  const hasInAppHistory =
    history.length > 1 && !fromExternal && previousPage !== '/login';

  const goBack = (fallbackPath?: string) => {
    if (!hasInAppHistory && fallbackPath) {
      navigate(fallbackPath);
      return;
    }
    navigate(-1);
  };

  return (
    <BackButtonContext.Provider
      value={{ isVisible, hasInAppHistory, setIsVisible, goBack }}
    >
      {children}
    </BackButtonContext.Provider>
  );
};

// Custom hook to use the BackButtonContext
export const useBackButton = (): BackButtonContextType => {
  const context = useContext(BackButtonContext);
  if (context === undefined) {
    throw new Error('useBackButton must be used within a BackButtonProvider');
  }
  return context;
};
